import { useCallback,useEffect } from 'react';

import { GridRowModel, GridValidRowModel } from '@mui/x-data-grid';
import { AsyncThunk, SerializedError } from '@reduxjs/toolkit';
import { useAppDispatch,useAppSelector } from 'app/hooks';
import type { RootState } from 'app/store';
import { setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { RequestParamsBase } from 'common/services/api/Models/CommonRequestModels';
import { handleListFetchError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

export function usePalmFetchEntitiesWithExtraReducers<TEntity extends GridValidRowModel, TThunkParams extends RequestParamsBase >(
    entityName: string,
    selectEntities: (state: RootState) => GridRowModel[],
    selectError: (state: RootState) => SerializedError | undefined,
    clearEntities: () => {
        payload: undefined;
        type: string;
    },
    fetchEntitiesThunk: AsyncThunk<TEntity[], TThunkParams, Record<string, unknown>>,
    thunkParams: TThunkParams
) {

    const entities = useAppSelector(selectEntities);
    const error = useAppSelector(selectError);

    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    // Handle real errors (AbortError is filtered out at the slice level)
    if (error?.message && error?.name !== 'AbortError') {
        handleListFetchError(extractStatusCode(error?.message), entityName, enqueueSnackbar);
    }

    useEffect(() => {
        dispatch(setRows(entities));
    }, [entities]);

    const fetchEntities = useCallback((signal?: AbortSignal) => {
        const params = {
            ...thunkParams,
            signal,
        } as TThunkParams;
        dispatch(fetchEntitiesThunk(params as TThunkParams & undefined));
    }, [thunkParams]);

    useEffect(() => {
        const abortController = new AbortController();
        const { signal } = abortController;

        fetchEntities(signal);
        return () => {
            dispatch(clearEntities());
            abortController.abort();
        };
    }, []);

    return fetchEntities;

}
